import { useRef, useState } from 'react';
import cn from 'clsx';
import { makeStyles, useTheme } from '@material-ui/styles';
import ExclamationCircleLine from '../../icons/ExclamationCircleLine';
import Tooltip from '../../tooltip';
import { Theme } from '../../../theme/types';

type FieldErrorProps = {
    error: string;
    boundariesElement?: HTMLElement | string;
};

const FieldError: React.FC<FieldErrorProps> = ({ error, boundariesElement }) => {
    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(true);
    const classes = useStyles();
    const theme = useTheme<Theme>();
    const buttonClasses = cn(classes.button, isTooltipOpen && 'is-active');
    const toggleTooltip = () => setTooltipOpen(!isTooltipOpen);
    const anchorRef = useRef<HTMLButtonElement | null>(null);

    return (
        <div className={classes.container}>
            <Tooltip
                open={isTooltipOpen}
                anchorRef={anchorRef}
                setOpen={setTooltipOpen}
                text={error}
                customTextColor={theme.palette.primary[500]}
                customBackgroundColor={theme.palette.primary[100]}
                mainElementProps={{ role: 'alert' }}
                clickAwayDisabled={true}
                boundariesElement={boundariesElement}
            >
                <button
                    type="button"
                    className={buttonClasses}
                    onClick={toggleTooltip}
                    aria-label="Toggle error message"
                    data-testid="error-icon"
                    ref={anchorRef}
                >
                    <ExclamationCircleLine className={classes.icon} />
                </button>
            </Tooltip>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        container: {
            position: 'relative',
            display: 'inline-flex',
            marginLeft: 8,
        },
        button: {
            display: 'inline-flex',
            transition: 'box-shadow 300ms ease-in-out',
            borderRadius: '50%',
            border: 0,
            background: 'none',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            fontFamily: 'inherit',

            '&:hover, &.is-active': {
                boxShadow: `0 0 0 4px ${theme.palette.neutral[200]}`,
            },
        },
        icon: {
            color: theme.palette.primary[500],
            fontSize: 14,
        },
    }),
    { classNamePrefix: 'field-error' }
);

export default FieldError;
